import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { LoadingState } from "@/components/base/LoadingState";
import { Modal } from "@/components/base/Modal";
import {
  type EbayAspectMapping,
  resolveAspectValue,
  EBAY_OPTION_NAME_MAP,
} from "@/lib/providers/ebay-adapter";

interface ProductAttributeValueRow {
  value: string | null;
  product_attributes?: { name?: string | null } | null;
}

interface EbayAspectTabProps {
  listingId: string;
  orgId: string;
  connectorId: string;
  categoryId?: string | null;
}

export default function EbayAspectTab({ listingId, orgId, connectorId, categoryId }: EbayAspectTabProps) {
  const [mappingOpen, setMappingOpen] = useState(false);
  const [selectedAspect, setSelectedAspect] = useState<string | null>(null);

  const { data: aspectCache, isLoading } = useQuery({
    queryKey: ["ebay-aspects", orgId, connectorId, categoryId],
    enabled: !!orgId && !!connectorId && !!categoryId,
    queryFn: async () => {
      const { data } = await supabase
        .from("ebay_aspect_cache")
        .select("*")
        .eq("connector_id", connectorId)
        .eq("category_id", categoryId)
        .order("required", { ascending: false });
      return (data ?? []) as Array<{
        id: string;
        aspect_name: string;
        aspect_localized_name: string | null;
        required: boolean;
        recommended: boolean;
        aspect_type: string;
        allowed_values: string[];
        variation_aspect: boolean;
      }>;
    },
  });

  const { data: mappings } = useQuery({
    queryKey: ["ebay-aspect-mappings", orgId, listingId],
    enabled: !!orgId && !!listingId,
    queryFn: async () => {
      const { data } = await supabase
        .from("ebay_aspect_mappings")
        .select("*")
        .eq("listing_id", listingId);
      return (data ?? []) as EbayAspectMapping[];
    },
  });

  const { data: product } = useQuery({
    queryKey: ["ebay-aspect-product", orgId, listingId],
    enabled: !!orgId && !!listingId,
    queryFn: async () => {
      const { data: listing } = await supabase
        .from("channel_listings")
        .select("product_id")
        .eq("id", listingId)
        .eq("org_id", orgId)
        .maybeSingle();
      if (!listing?.product_id) return null;
      const { data } = await supabase
        .from("products")
        .select("id, name, brand, description, condition, mpn, manufacturer, model_number")
        .eq("id", listing.product_id)
        .maybeSingle();
      return data;
    },
  });

  const { data: attributes } = useQuery({
    queryKey: ["ebay-aspect-attributes", orgId, listingId],
    enabled: !!orgId && !!listingId,
    queryFn: async () => {
      const { data: listing } = await supabase
        .from("channel_listings")
        .select("product_id")
        .eq("id", listingId)
        .eq("org_id", orgId)
        .maybeSingle();
      if (!listing?.product_id) return [];
      const { data } = await supabase
        .from("product_attribute_values")
        .select("*, product_attributes(id, name)")
        .eq("product_id", listing.product_id);
      return (data ?? []).map((d: ProductAttributeValueRow) => ({
        attribute_name: d.product_attributes?.name || "",
        value: d.value || "",
      }));
    },
  });

  const { data: identifiers } = useQuery({
    queryKey: ["ebay-aspect-identifiers", orgId, listingId],
    enabled: !!orgId && !!listingId,
    queryFn: async () => {
      const { data: listing } = await supabase
        .from("channel_listings")
        .select("product_id")
        .eq("id", listingId)
        .eq("org_id", orgId)
        .maybeSingle();
      if (!listing?.product_id) return [];
      const { data } = await supabase
        .from("product_identifiers")
        .select("identifier_type, identifier_value, variant_id")
        .eq("product_id", listing.product_id);
      return (data ?? []) as Array<{ identifier_type: string; identifier_value: string; variant_id?: string | null }>;
    },
  });

  if (isLoading) return <LoadingState />;

  if (!categoryId) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700">
        <i className="ri-information-line mr-1" />
        Select an eBay category to view aspect requirements and configure mappings.
      </div>
    );
  }

  if (!aspectCache || aspectCache.length === 0) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-500">
        No aspect requirements found for this category. Connect to eBay and sync categories to discover aspects.
      </div>
    );
  }

  const getMapping = (aspectName: string) => mappings?.find((m) => m.aspectName === aspectName);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">eBay Aspect Mapping</h3>
          <p className="text-xs text-slate-400">
            Map eBay category aspects to canonical Tradivo fields
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Aspect</th>
                <th className="px-4 py-3 font-medium">Requirement</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Source</th>
                <th className="px-4 py-3 font-medium">Resolved Value</th>
                <th className="px-4 py-3 font-medium">Variation</th>
                <th className="px-4 py-3 font-medium w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {aspectCache.map((aspect) => {
                const mapping = getMapping(aspect.aspect_name);
                const resolved = mapping
                  ? resolveAspectValue(
                      mapping,
                      product || {},
                      attributes || [],
                      {},
                      identifiers || []
                    )
                  : null;
                const isMissing = aspect.required && !resolved;

                return (
                  <tr key={aspect.id} className={isMissing ? "bg-red-50/50" : ""}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-slate-800">
                        {aspect.aspect_localized_name || aspect.aspect_name}
                      </p>
                      <p className="text-xs text-slate-400">{aspect.aspect_name}</p>
                    </td>
                    <td className="px-4 py-3">
                      {aspect.required ? (
                        <span className="text-xs bg-red-50 text-red-700 px-1.5 py-0.5 rounded-full">Required</span>
                      ) : aspect.recommended ? (
                        <span className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full">Recommended</span>
                      ) : (
                        <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">Optional</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-slate-500">{aspect.aspect_type}</span>
                    </td>
                    <td className="px-4 py-3">
                      {mapping ? (
                        <span className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full">
                          {mapping.sourceType.replace(/_/g, " ")}
                          {mapping.sourceKey ? `: ${mapping.sourceKey}` : ""}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">Not mapped</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {resolved ? (
                        <span className="text-sm text-slate-700">{resolved}</span>
                      ) : (
                        <span className="text-xs text-red-500">{isMissing ? "Missing" : "—"}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {aspect.variation_aspect ? (
                        <span className="text-xs bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full">Yes</span>
                      ) : (
                        <span className="text-xs text-slate-400">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => { setSelectedAspect(aspect.aspect_name); setMappingOpen(true); }}
                        className="text-xs font-medium text-emerald-600 hover:text-emerald-700 whitespace-nowrap"
                      >
                        {mapping ? "Edit" : "Map"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Value mapping helper */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h4 className="text-sm font-semibold text-slate-800 mb-2">Canonical → eBay Value Mappings</h4>
        <div className="space-y-2 text-sm">
          {Object.entries(EBAY_OPTION_NAME_MAP).map(([canonical, ebay]) => (
            <div key={canonical} className="flex items-center gap-2">
              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">{canonical}</span>
              <i className="ri-arrow-right-line text-slate-400 text-xs" />
              <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded">{ebay}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Mapping Modal */}
      <Modal
        open={mappingOpen}
        onClose={() => setMappingOpen(false)}
        title={`Map Aspect: ${selectedAspect}`}
        footer={
          <>
            <button onClick={() => setMappingOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={() => setMappingOpen(false)} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap">Save Mapping</button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Source Type</label>
            <select className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
              <option value="">Select source...</option>
              <option value="product_field">Product Field (name, brand, description...)</option>
              <option value="product_attribute">Product Attribute</option>
              <option value="product_option">Product Option (Colour, Size...)</option>
              <option value="product_identifier">Product Identifier (GTIN, MPN...)</option>
              <option value="listing_override">Listing Override</option>
              <option value="fixed_value">Fixed Value</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Source Key / Field Name</label>
            <input className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" placeholder="e.g. brand, Colour, gtin" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Override Value (optional)</label>
            <input className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" placeholder="Fixed value or override" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="variation_aspect" className="rounded border-slate-300" />
            <label htmlFor="variation_aspect" className="text-xs text-slate-600">This is a variation aspect</label>
          </div>
        </div>
      </Modal>
    </div>
  );
}